import type { RequiredGeneric } from '@pba/react-utils';
import type { EventPrivacy } from 'models';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Alert, CheckboxInput, Form, Icon, Input, Section, TagsInput, Text, Title, Tooltip } from 'ui';
import type { CommunityOption } from '../EventCommunity';

type RequiredFormValues = {
  community?: CommunityOption;
  featuredInChannels?: boolean;
  organizationId?: string;
  showInChannels?: boolean;
  tags?: string[];
  viewPermission: EventPrivacy;
};

const EventChannelsOptions = <FormValues extends RequiredFormValues>(_: RequiredGeneric<FormValues>) => {
  const { t } = useTranslation();
  const { canViewChannelsSection } = usePermissions();

  return (
    <>
      <Form.Item<RequiredFormValues> hidden name="organizationId">
        <Input />
      </Form.Item>
      <Form.Item<RequiredFormValues> dependencies={['organizationId', 'viewPermission']} noStyle>
        {({ getFieldsValue, setFieldValue }) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          const { community, organizationId, viewPermission } = getFieldsValue(true) as Partial<RequiredFormValues>;

          const organization = community?.data.organization;

          const isCommunitySecret = community?.data.privacy === 'secret';
          const isPublic = viewPermission === 'public';

          const channelType = organization?.customChannels ? 'custom' : 'default';

          if (community && canViewChannelsSection({ organizationId }) && !isCommunitySecret && isPublic) {
            return (
              <>
                <Title level={4} marginBottom>
                  Channels
                </Title>
                <Section lineBottom={false}>
                  <Alert
                    title={
                      <>
                        <Text>The selected community is part of an organization that is using</Text>{' '}
                        <Text strong>{t(channelType)}</Text> <Text>channels</Text>
                      </>
                    }
                  />
                </Section>
                <TagsInput
                  input={{
                    emptyProps: {
                      description: t('The selected community is in an organization that has no tags defined.'),
                    },
                    options: organization?.tags.map((tagItem) => ({
                      label: tagItem.name,
                      value: tagItem.id,
                    })),
                  }}
                  item={{ label: 'Tags', name: 'tags' }}
                />
                <CheckboxInput
                  input={{
                    onChange() {
                      // TODO find better approach - `setFieldValue` does not inherit type from `FormValues`
                      setFieldValue('featuredInChannels', false);
                    },
                  }}
                  item={{
                    label: (
                      <Text>
                        {t('Show event in Channels')}{' '}
                        <Tooltip title="Event will display in your organization’s channels. Everyone in your organization will be able to join the event.">
                          <Icon icon="information-line" />
                        </Tooltip>
                      </Text>
                    ),
                    name: 'showInChannels',
                    valuePropName: 'checked',
                  }}
                />
                <Form.Item<RequiredFormValues> dependencies={['showInChannels']} noStyle>
                  {({ getFieldsValue: showInChannelsGetFieldsValue }) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                    const { showInChannels: isShownInChannels } = showInChannelsGetFieldsValue(
                      true,
                    ) as Partial<RequiredFormValues>;

                    if (isShownInChannels) {
                      return (
                        <CheckboxInput
                          item={{
                            label: (
                              <Text>
                                {t('Pin event as featured in Channels')}{' '}
                                <Tooltip title="Event will appear as featured in Channels.">
                                  <Icon icon="information-line" />
                                </Tooltip>
                              </Text>
                            ),
                            name: 'featuredInChannels',
                            valuePropName: 'checked',
                          }}
                        />
                      );
                    }

                    return null;
                  }}
                </Form.Item>
              </>
            );
          }

          return null;
        }}
      </Form.Item>
    </>
  );
};

export default EventChannelsOptions;
